/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2024] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { Radio } from 'backbone';

import PageableCollection from '@/backbone/PageableCollection';
import ButtonCell from '@/backgrid/ButtonCell';
import StringCell from '@/backgrid/StringCell';
import { DateCell } from '@/backgrid/MomentCell';
import GridView from '@/views/GridView';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import LinkCell from '@/backgrid/LinkCell';

import ClaimOrder from '../../services/ClaimOrder';

const InProgressOrdersGridView = GridView.extend({
  initialize() {
    const self = this;
    this.options.columns = [
      {
        name: 'orderID',
        label: 'Order ID',
        cell: StringCell,
        headerCell: 'custom',
        width: 8,
        editable: false
      },
      {
        name: 'assignedCrs',
        label: 'Assigned CRS',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false
      },
      {
        name: 'orderType',
        label: 'Order Type',
        cell: StringCell,
        headerCell: 'custom',
        width: 14,
        editable: false
      },
      {
        name: 'orderStatus',
        label: 'Order Status',
        cell: StringCell,
        headerCell: 'custom',
        width: 8,
        editable: false
      },
      {
        name: 'serialNumber',
        label: 'Serial Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false
      },
      {
        name: 'command',
        label: 'Command',
        cell: LinkCell.extend({
          name() {
            return this.model.get('command');
          },
          href(model) {
            return `#command/${model.get('commandId')}`;
          },
          newTab: true
        }),
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'replacementPIINSPIIN',
        label: 'Contract #',
        cell: StringCell,
        headerCell: 'custom',
        width: 8,
        editable: false
      },
      {
        name: 'region',
        label: 'Region',
        cell: StringCell,
        headerCell: 'custom',
        width: 8,
        editable: false
      },
      {
        name: 'popStartDate',
        label: 'POP Start Date',
        cell: DateCell,
        headerCell: 'custom',
        width: 8,
        editable: false
      },
      {
        name: 'submittedOn',
        label: 'Submitted On',
        cell: DateCell,
        headerCell: 'custom',
        width: 8,
        editable: false,
        direction: 'ascending'
      },
      {
        name: 'openClaimButton',
        label: '',
        width: 8,
        cell: ButtonCell.extend({
          name() {
            return this.model.get('assignedCrs') ? 'Open' : 'Claim';
          },
          onClick(e) {
            this.model.get('assignedCrs') ? self.viewDetails(e, this) : self.onClickClaim(e, this);
          },
          buttonClass() {
            return this.model.get('assignedCrs')
              ? 'btn-sm btn-outline-primary'
              : 'btn-sm btn-outline-success text-white-hover';
          }
        }),
        editable: false,
        sortable: false
      }
    ];
    const orders = this.model.get('inProgressOrders');
    this.options.collection = new PageableCollection(orders, { pageSize: 10 });
    this.options.emptyText = 'No In-Progress Orders';
  },

  showClaimSuccessMessage(orderId) {
    showSuccessMessage({
      code: 'claimSuccess',
      title: 'Order Claimed',
      message: `Order ${orderId} has been successfully claimed`,
      hideAfter: 5
    });
  },

  showClaimErrorMessage(orderId) {
    showErrorMessage({
      errorCode: 'claimError',
      errorTitle: 'Failed to Claim Order',
      errorMessage: `An error occurred while attempting to claim order ${orderId}. Try again or contact an administrator if your issue persists.`
    });
  },

  async onClickClaim(e, self) {
    e.preventDefault();
    const orderId = self.model.get('orderID');

    try {
      await ClaimOrder(orderId);
      this.showClaimSuccessMessage(orderId);
      Radio.channel('navigate').request('show:order', orderId);
    } catch (e) {
      this.showClaimErrorMessage(orderId);
    }
  },

  viewDetails(e, self) {
    e.preventDefault();
    Radio.channel('navigate').request('show:order', self.model.get('orderID'));
  }
});

export default InProgressOrdersGridView;
